/**
 * GuideAgenda - Interactive calendar component for guide availability management
 * Allows guides to:
 * - View their bookings
 * - Block/unblock dates
 * - Set availability windows
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Users, 
  MapPin, 
  Plus, 
  X, 
  Check,
  ChevronLeft,
  ChevronRight,
  Lock,
  Unlock,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format, isSameDay, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isAfter, isBefore } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

// Helper to get API URL - uses current origin in production
const getApiUrl = () => {
  if (typeof window !== 'undefined' && window.location.origin.includes('xperience-guides.com')) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};
const API_URL = getApiUrl() + '/api';

const GuideAgenda = ({ userId }) => {
  const { i18n } = useTranslation();
  const locale = i18n.language === 'fr' ? fr : enUS;
  
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch bookings and blocked dates
  useEffect(() => {
    fetchData();
  }, [currentMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('vgt_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Fetch bookings for the guide
      const bookingsRes = await axios.get(`${API_URL}/bookings/guide`, {
        headers,
        withCredentials: true
      });
      
      setBookings(bookingsRes.data || []);
      
      // Fetch blocked dates
      const blockedRes = await axios.get(`${API_URL}/explorer/blocked-dates`, {
        headers,
        withCredentials: true
      });
      
      setBlockedDates(blockedRes.data?.blocked_dates || []);
    } catch (error) {
      console.error('Error fetching agenda data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Toggle date block status
  const toggleBlockDate = async (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const isBlocked = blockedDates.includes(dateStr);
    
    setSaving(true);
    try {
      const token = localStorage.getItem('vgt_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      if (isBlocked) {
        // Unblock the date
        await axios.post(`${API_URL}/explorer/unblock-date`, { date: dateStr }, {
          headers,
          withCredentials: true
        });
        setBlockedDates(prev => prev.filter(d => d !== dateStr));
        toast.success(i18n.language === 'fr' ? 'Date débloquée' : 'Date unblocked');
      } else {
        // Block the date
        await axios.post(`${API_URL}/explorer/block-date`, { date: dateStr }, {
          headers,
          withCredentials: true
        });
        setBlockedDates(prev => [...prev, dateStr]);
        toast.success(i18n.language === 'fr' ? 'Date bloquée' : 'Date blocked');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating date');
    } finally {
      setSaving(false);
    }
  };

  // Get bookings for a specific date
  const getBookingsForDate = (date) => {
    return bookings.filter(booking => {
      const bookingDate = new Date(booking.date);
      return isSameDay(bookingDate, date);
    });
  };

  // Check if date is blocked
  const isDateBlocked = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return blockedDates.includes(dateStr);
  };

  // Check if date has bookings
  const hasBookings = (date) => {
    return getBookingsForDate(date).length > 0;
  };

  // Get status badge for booking
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: i18n.language === 'fr' ? 'En attente' : 'Pending' },
      confirmed: { bg: 'bg-blue-100', text: 'text-blue-800', label: i18n.language === 'fr' ? 'Confirmée' : 'Confirmed' },
      paid: { bg: 'bg-green-100', text: 'text-green-800', label: i18n.language === 'fr' ? 'Payée' : 'Paid' },
      completed: { bg: 'bg-gray-100', text: 'text-gray-800', label: i18n.language === 'fr' ? 'Terminée' : 'Completed' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: i18n.language === 'fr' ? 'Annulée' : 'Cancelled' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  // Custom day renderer
  const renderDay = (date) => {
    const dayBookings = getBookingsForDate(date);
    const blocked = isDateBlocked(date);
    const isPast = isBefore(date, new Date()) && !isSameDay(date, new Date());
    
    return (
      <div 
        className={`relative w-full h-full min-h-[60px] p-1 border rounded-lg transition-all cursor-pointer
          ${blocked ? 'bg-red-50 border-red-200' : 'border-gray-200 hover:border-primary/50'}
          ${selectedDate && isSameDay(date, selectedDate) ? 'ring-2 ring-primary' : ''}
          ${isPast ? 'opacity-50' : ''}
        `}
        onClick={() => setSelectedDate(date)}
      >
        <div className="flex justify-between items-start">
          <span className={`text-sm font-medium ${isSameDay(date, new Date()) ? 'text-primary' : 'text-gray-700'}`}>
            {format(date, 'd')}
          </span>
          {blocked && <Lock className="w-3 h-3 text-red-500" />}
        </div>
        
        {dayBookings.length > 0 && (
          <div className="mt-1">
            {dayBookings.slice(0, 2).map((booking, idx) => (
              <div 
                key={idx} 
                className="text-xs bg-primary/10 text-primary rounded px-1 py-0.5 mb-0.5 truncate"
                title={booking.experience_title}
              >
                {booking.participants}p
              </div>
            ))}
            {dayBookings.length > 2 && (
              <div className="text-xs text-gray-500">+{dayBookings.length - 2}</div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Generate calendar grid
  const generateCalendarGrid = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });
    
    // Add padding days for the start of the month
    const startDayOfWeek = start.getDay();
    const paddingDays = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1; // Monday = 0
    
    return { days, paddingDays };
  };

  const { days, paddingDays } = generateCalendarGrid();
  const selectedDateBookings = selectedDate ? getBookingsForDate(selectedDate) : [];

  return (
    <div className="bg-white rounded-xl shadow-sm border">
      {/* Header */}
      <div className="p-4 border-b bg-gradient-to-r from-primary/5 to-orange-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-primary" />
            <h2 className="font-semibold text-lg">
              {i18n.language === 'fr' ? 'Mon Agenda' : 'My Agenda'}
            </h2>
          </div>
          
          {/* Month Navigation */}
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-medium min-w-[150px] text-center capitalize">
              {format(currentMonth, 'MMMM yyyy', { locale })}
            </span>
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Legend */}
        <div className="flex items-center gap-4 mt-3 text-xs text-gray-600">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-primary/20 rounded"></div>
            <span>{i18n.language === 'fr' ? 'Réservation' : 'Booking'}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-red-100 rounded"></div>
            <span>{i18n.language === 'fr' ? 'Bloqué' : 'Blocked'}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 border-2 border-primary rounded"></div>
            <span>{i18n.language === 'fr' ? 'Sélectionné' : 'Selected'}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* Calendar Grid */}
        <div className="flex-1 p-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {(i18n.language === 'fr' 
                  ? ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
                  : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
                ).map(day => (
                  <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {/* Padding cells */}
                {Array.from({ length: paddingDays }).map((_, idx) => (
                  <div key={`pad-${idx}`} className="min-h-[60px]"></div>
                ))}
                
                {/* Day cells */}
                {days.map(day => (
                  <div key={day.toISOString()}>
                    {renderDay(day)}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Selected Date Details */}
        <div className="lg:w-80 border-t lg:border-t-0 lg:border-l p-4 bg-gray-50">
          {selectedDate ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold capitalize">
                  {format(selectedDate, 'EEEE d MMMM', { locale })}
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleBlockDate(selectedDate)}
                  disabled={saving || hasBookings(selectedDate)}
                  className={isDateBlocked(selectedDate) ? 'text-green-600' : 'text-red-600'}
                >
                  {isDateBlocked(selectedDate) ? (
                    <>
                      <Unlock className="w-3 h-3 mr-1" />
                      {i18n.language === 'fr' ? 'Débloquer' : 'Unblock'}
                    </>
                  ) : (
                    <>
                      <Lock className="w-3 h-3 mr-1" />
                      {i18n.language === 'fr' ? 'Bloquer' : 'Block'}
                    </>
                  )}
                </Button>
              </div>
              
              {isDateBlocked(selectedDate) && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <div className="flex items-center gap-2 text-red-700">
                    <Lock className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {i18n.language === 'fr' ? 'Cette date est bloquée' : 'This date is blocked'}
                    </span>
                  </div>
                  <p className="text-xs text-red-600 mt-1">
                    {i18n.language === 'fr' 
                      ? 'Aucune réservation ne peut être faite ce jour.' 
                      : 'No bookings can be made on this day.'}
                  </p>
                </div>
              )}
              
              {selectedDateBookings.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    {selectedDateBookings.length} {i18n.language === 'fr' ? 'réservation(s)' : 'booking(s)'}
                  </p>
                  
                  {selectedDateBookings.map((booking, idx) => (
                    <div key={idx} className="bg-white rounded-lg border p-3 shadow-sm">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-medium text-sm line-clamp-1">{booking.experience_title}</h4>
                        {getStatusBadge(booking.status)}
                      </div>
                      
                      <div className="space-y-1 text-xs text-gray-600">
                        <div className="flex items-center gap-2">
                          <Users className="w-3 h-3" />
                          <span>{booking.participants} {i18n.language === 'fr' ? 'participant(s)' : 'participant(s)'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-primary">
                            {booking.total_price}€
                          </span>
                        </div>
                        {booking.experimenter_name && (
                          <div className="flex items-center gap-2">
                            <span>{i18n.language === 'fr' ? 'Client:' : 'Client:'} {booking.experimenter_name}</span>
                          </div>
                        )}
                      </div>
                      
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full mt-2"
                        onClick={() => window.location.href = `/bookings/${booking.booking_id}`}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        {i18n.language === 'fr' ? 'Voir détails' : 'View details'}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">
                    {i18n.language === 'fr' ? 'Aucune réservation ce jour' : 'No bookings this day'}
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <CalendarIcon className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p className="text-sm">
                {i18n.language === 'fr' 
                  ? 'Sélectionnez une date pour voir les détails' 
                  : 'Select a date to see details'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuideAgenda;
